import { useEffect, useState } from "react";
import { Loader2, Plus, X, Sparkles, PenLine, CheckCircle2, FileText, History } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/ui";
import { cn } from "@/lib/utils";

const STATUS = { draft: "#155EEF", "pending-review": "#F79009", signed: "#12B76A" };

export default function Documents() {
  const [docs, setDocs] = useState(null);
  const [residents, setResidents] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ resident_id: "", content: "", type: "Progress Note" });
  const [assist, setAssist] = useState(null);
  const [assisting, setAssisting] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/documents").then((r) => setDocs(r.data)).catch(() => setDocs([]));
  useEffect(() => { load(); api.get("/residents").then((r) => setResidents(r.data)).catch(() => {}); }, []);

  const runAssist = async () => {
    if (!form.content.trim()) { toast.error("Write a draft first"); return; }
    setAssisting(true);
    try {
      const { data } = await api.post("/ai/document-assist", { content: form.content, resident_id: form.resident_id });
      setAssist(data);
    } catch (e) { toast.error("AI assist failed"); } finally { setAssisting(false); }
  };

  const save = async () => {
    if (!form.resident_id || !form.content.trim()) { toast.error("Resident and note content required"); return; }
    setSaving(true);
    try { await api.post("/documents", form); toast.success("Note saved as draft");
      setOpen(false); setForm({ resident_id: "", content: "", type: "Progress Note" }); setAssist(null); load(); }
    catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const sign = async (id) => {
    try { await api.patch(`/documents/${id}`, { status: "signed" }); toast.success("Document signed"); load(); }
    catch (e) { toast.error("Sign failed"); }
  };

  if (!docs) return <div className="flex h-64 items-center justify-center text-muted-foreground"><Loader2 className="animate-spin" /></div>;
  const filtered = filter === "all" ? docs : docs.filter((d) => d.status === filter);

  return (
    <div>
      <PageHeader overline="Governance · Documentation Intelligence" title="Documentation"
        description="AI-assisted progress notes with missing-information detection, ISP linkage and supervisor sign-off. AI never invents clinical facts."
        actions={
          <button data-testid="new-doc-btn" onClick={() => setOpen(true)}
            className="flex items-center gap-2 rounded-md bg-[#155EEF] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#0F4FD1]">
            <Plus size={16} /> New Note
          </button>} />

      <div className="mb-3 flex gap-2">
        {["all", "draft", "pending-review", "signed"].map((f) => (
          <button key={f} data-testid={`doc-filter-${f}`} onClick={() => setFilter(f)}
            className={cn("rounded-full border px-3 py-1 text-xs font-medium capitalize transition-colors",
              filter === f ? "border-[#155EEF] bg-[#155EEF] text-white" : "bg-card hover:border-[#155EEF]")}>
            {f.replace("-", " ")}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {filtered.map((d) => (
          <div key={d.id} data-testid={`doc-${d.id}`} className="rounded-xl border bg-card p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold"><FileText size={15} className="text-[#155EEF]" /> {d.type}</div>
              <span className="rounded-full px-2 py-0.5 text-xs font-semibold capitalize" style={{ color: STATUS[d.status], backgroundColor: STATUS[d.status] + "1a" }}>{d.status?.replace("-", " ")}</span>
            </div>
            <div className="mt-1 text-xs text-muted-foreground">{d.resident_name} · {d.author} · <span className="inline-flex items-center gap-1"><History size={11} /> v{d.version || 1}</span></div>
            <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{d.content}</p>
            <div className="mt-3 flex items-center justify-between">
              {d.signed_by ? <span className="flex items-center gap-1 text-xs font-medium text-[#12B76A]"><CheckCircle2 size={13} /> Signed by {d.signed_by}</span>
                : <span className="text-xs text-muted-foreground">Awaiting sign-off</span>}
              {d.status !== "signed" && (
                <button data-testid={`sign-${d.id}`} onClick={() => sign(d.id)}
                  className="flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium hover:border-[#12B76A] hover:text-[#12B76A]"><PenLine size={13} /> Sign</button>
              )}
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="careiq-scroll relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border bg-card p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold">New Progress Note</h3>
              <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground"><X size={20} /></button>
            </div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Resident</label>
            <select data-testid="doc-resident" value={form.resident_id} onChange={(e) => setForm({ ...form, resident_id: e.target.value })} className="docinput mb-3">
              <option value="">Select resident…</option>
              {residents.map((r) => <option key={r.id} value={r.id}>{r.first_name} {r.last_name}</option>)}
            </select>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Note</label>
            <textarea data-testid="doc-content" rows={5} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
              placeholder="Describe the shift, participation, mood and any observations…" className="docinput resize-none" />
            <button data-testid="doc-assist-btn" onClick={runAssist} disabled={assisting}
              className="mt-3 flex items-center gap-2 rounded-md bg-[#7C3AED] px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#6D28D9] disabled:opacity-60">
              {assisting ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} AI Documentation Assist
            </button>

            {assist && (
              <div className="mt-4 space-y-3 rounded-lg border bg-background p-4" style={{ borderLeft: "3px solid #7C3AED" }}>
                <div>
                  <div className="overline mb-1">Polished note (review before saving)</div>
                  <p className="whitespace-pre-wrap text-sm">{assist.improved}</p>
                  <button onClick={() => setForm({ ...form, content: assist.improved })}
                    className="mt-2 rounded-md border px-2.5 py-1 text-xs font-medium hover:border-[#7C3AED] hover:text-[#7C3AED]">Use this text</button>
                </div>
                {assist.missing?.length > 0 && <Chips label="Missing information" items={assist.missing} color="#F79009" testid="doc-missing" />}
                {assist.isp_links?.length > 0 && <Chips label="ISP goal links" items={assist.isp_links} color="#155EEF" />}
                {assist.compliance?.length > 0 && <Chips label="Compliance flags" items={assist.compliance} color="#12B76A" />}
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="rounded-md border px-4 py-2 text-sm font-medium">Cancel</button>
              <button data-testid="save-doc-btn" onClick={save} disabled={saving}
                className="rounded-md bg-[#155EEF] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0F4FD1] disabled:opacity-60">Save Draft</button>
            </div>
          </div>
        </div>
      )}
      <style>{`.docinput{width:100%;border-radius:0.5rem;border:1px solid hsl(var(--border));background:hsl(var(--background));padding:0.55rem 0.75rem;font-size:0.875rem;outline:none}.docinput:focus{border-color:#155EEF}`}</style>
    </div>
  );
}

function Chips({ label, items, color, testid }) {
  return (
    <div data-testid={testid}>
      <div className="overline mb-1">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it, i) => <span key={i} className="rounded-md px-2 py-1 text-xs font-medium" style={{ color, backgroundColor: color + "15" }}>{it}</span>)}
      </div>
    </div>
  );
}
